import type { DocumentBodyLoader } from '../../lib/documentBody';
/**
 * DocViewerDialog — single modal that previews every common file
 * type, dispatched via resolveViewer() in docViewers/index.tsx.
 *
 * Each type-specific renderer is React.lazy (wrapped in retryImport,
 * which retries the chunk fetch once) so the heavyweight deps
 * (pdfjs-dist, mammoth, xlsx) only fetch when the user actually opens
 * a doc of that type. The Suspense fallback covers the chunk fetch;
 * the renderer itself shows its own internal loading state once the
 * chunk has resolved.
 *
 * DocViewerErrorBoundary wraps the whole mount: a chunk fetch that
 * fails even after the retry (stale deploy) — or any runtime crash
 * inside the viewer — lands on a recovery panel (Refresh / Download
 * original / View extracted text) rather than an unmounted blank.
 */
import React, { Suspense } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Download, FileText, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { downloadDocument } from '../../lib/documentDownload';
import { createSiteDocumentOriginalLoader, type SiteDocumentOriginalContext } from '../../lib/siteDocumentOriginalClient';
import { auth } from '../../firebase';
import { toast } from 'sonner';
import type { SiteDocument } from '../../types/siteRag';
import type { SedSectionId } from '../../types/siteEvidence';
import { resolveViewer } from './docViewers';
import { DocViewerErrorBoundary } from './docViewers/DocViewerErrorBoundary';
import { ExtractedTextDialog } from './docViewers/ExtractedTextDialog';
import { DocViewerActionsContext } from './docViewers/useDocBytes';
import { DocSedIngestionPopover } from './DocSedIngestionPopover';
import { useStore } from '../../store';

interface Props {
  binaryContext?: SiteDocumentOriginalContext;
  bodyLoader?: DocumentBodyLoader;
  doc: SiteDocument | null;
  open: boolean;
  onClose: () => void;
  /**
   * SET-D4 — when supplied, the dialog header renders a "How is this
   * document used?" info popover next to the file name. Callers that
   * have a SED reverse-index handy (Site Evidence tab) plumb the doc's
   * contributed sections through; callers that don't (e.g. legacy
   * report-mode DocumentLibrary uses) omit and the popover is hidden.
   */
  ingestionContributedSections?: SedSectionId[];
  /**
   * SET-D5 — when set, the dialog opens directly onto the extracted-
   * text view ("what the AI sees"). Completes the KSD4 deferral: the
   * DocumentContentsPanel "Show extracted text" button was mounted but
   * not wired; this prop is how the host (SiteEvidenceTab) tells the
   * dialog to land on the extracted-text panel instead of the file
   * preview.
   */
  initialView?: 'preview' | 'extractedText';
  /** SET-D5 — handler passed through to the nested ExtractedTextDialog's
   *  Re-extract button. Wired by SiteEvidenceTab to KSD5
   *  `reExtractDocForSed`. Omit and the button is hidden. */
  onReExtractFromExtractedText?: (doc: SiteDocument) => void | Promise<void>;
  /**
   * REF-LINEAGE REF-3 — when supplied, PDF viewers open at this page
   * (1-based). The host (`RefLink` via the report renderer) passes the
   * page from `LinkedDocumentRef.pageNumber`. Non-PDF viewers ignore
   * the prop and open at the start. PdfViewer re-fires its page setter
   * when `doc.id` or `initialPage` changes while the dialog is mounted
   * (mirrors the `initialView` re-fire below).
   */
  initialPage?: number;
  /**
   * H-4 (passage-offset citation deep-links) — when supplied, the dialog
   * opens directly onto the extracted-text view ("what the AI sees") with
   * this `[start, end)` char span highlighted + scrolled into view. This
   * is the OFFSET-precision deep-link target: the span indexes
   * `doc.textContent`, which is exactly what that view renders, so the
   * user lands on the precise passage a `[Ref N]` was derived from.
   *
   * Plumbed by `RefLink` when `resolveLinkedDocDeepLink` returns
   * `precision === 'offset'`. Omit (page-precision / legacy callers) and
   * the dialog opens the file preview at `initialPage` exactly as before.
   */
  highlightOffset?: { start: number; end: number };
}

export const DocViewerDialog: React.FC<Props> = ({
  doc,
  open,
  onClose,
  ingestionContributedSections,
  initialView,
  onReExtractFromExtractedText,
  initialPage,
  highlightOffset,
  bodyLoader,
  binaryContext,
}) => {
  const [binaryAccountUid, setBinaryAccountUid] = React.useState(auth.currentUser?.uid);
  React.useEffect(() => {
    if (!binaryContext) return;
    let stopped = false; let unsubscribe: (() => void) | undefined;
    void import('firebase/auth').then(({ onAuthStateChanged }) => {
      if (!stopped) unsubscribe = onAuthStateChanged(auth, user => setBinaryAccountUid(user?.uid));
    });
    return () => { stopped = true; unsubscribe?.(); };
  }, [binaryContext?.siteId, binaryContext?.ownerUid]);
  const binaryLoader = React.useMemo(() => binaryContext ? createSiteDocumentOriginalLoader(binaryContext) : undefined,
    [binaryContext?.siteId, binaryContext?.ownerUid, binaryAccountUid]);
  const onDownloadOriginal = React.useCallback((d: SiteDocument) => downloadDocument(d, binaryLoader), [binaryLoader]);
  // The extracted-text view ("what the AI sees") is reachable from the
  // header AND from the ErrorBoundary fallback. Hold it here so a viewer
  // crash doesn't take the escape hatch down with it.
  const [extractedTextDoc, setExtractedTextDoc] = React.useState<SiteDocument | null>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  // DVF-RU — bumped after a successful in-place re-upload so the viewer
  // subtree re-mounts and re-reads the freshly-stamped `rawStoragePath`
  // (the doc object is mutated in place by `saveDocument`; a key bump is
  // the reliable way to force `useDocBytes` to re-run its fetch effect
  // regardless of whether the parent re-derives `doc` from the store).
  const [reUploadNonce, setReUploadNonce] = React.useState(0);

  // Stable callback so the context value isn't a new object every render.
  const onViewExtractedText = React.useCallback((d: SiteDocument) => setExtractedTextDoc(d), []);

  // DVF-3 — wire the per-doc re-extraction action. ViewerRecovery uses
  // this to turn the silent-dead-end ("No extracted text" disabled link)
  // into a recovery path: bytes are downloaded, the legacy-build extractor
  // re-runs, and the patched textContent unblocks the "AI text" tab on
  // the next open. Surface a toast on error so the user sees something.
  const reExtractDocument = useStore(s => s.reExtractDocument);
  const onReExtract = React.useCallback(async (d: SiteDocument) => {
    try {
      await reExtractDocument(d.id);
    } catch (e) {
      toast.error((e as Error).message || 'Re-extraction failed');
      throw e;
    }
  }, [reExtractDocument]);

  // DVF-RU — in-place re-upload for legacy docs that have no stored
  // original (`rawStoragePath` unset → preview/download dead-ended with
  // "uploaded before raw-file persistence"). New uploads already persist
  // the raw file (every `saveDocument` call site passes the original
  // File), so this only ever fires for pre-fix docs. We re-save the
  // user-picked file under the SAME doc id/metadata: `saveDocument`
  // uploads the bytes and stamps `rawStoragePath` on the doc object, then
  // we persist that single field through the existing `patchSiteDocument`
  // action so both `site.documents[i]` and every `report.documents[i]`
  // stay in sync and the change survives a reload. The active doc object
  // is mutated in place by `saveDocument`, so the open viewer re-renders
  // against the freshly-stamped `rawStoragePath` on the next pass.
  const patchSiteDocument = useStore(s => s.patchSiteDocument);
  const onReUpload = React.useCallback(async (d: SiteDocument, file: File) => {
    const { saveDocument } = await import('../../services/knowledge/documentUpload');
    // #89 — locate the owning site (doc ids are user-unique) BEFORE the
    // save so we can pass its id through to `saveDocument`, gating the
    // DOCUMENT write by that site's edit-lock (assertSiteWritable). A
    // locked-site re-upload by a non-holder now throws before any bytes
    // are written, rather than silently mutating the locked library.
    const sites = useStore.getState().siteRag.sites;
    const owner = sites.find(s => (s.documents ?? []).some(doc => doc.id === d.id));
    const result = await saveDocument(d, file, owner?.id);
    // A too-large / upload-failed raw save leaves `rawStoragePath` unset
    // and returns a structured warning — surface it and bail so we don't
    // claim success or write a no-op patch.
    if (result?.rawUploadWarning || !d.rawStoragePath) {
      throw new Error(
        result?.rawUploadWarning?.message ||
          'Re-upload did not persist the original file. Try again.',
      );
    }
    // Persist the stamped path. patchSiteDocument is read-only-located
    // here: we only CALL the existing store action, never define new
    // store state.
    if (owner) {
      await patchSiteDocument(owner.id, d.id, { rawStoragePath: d.rawStoragePath });
    }
    // Force the viewer subtree to re-mount so useDocBytes re-runs against
    // the now-stamped rawStoragePath and shows the real preview.
    setReUploadNonce(n => n + 1);
  }, [patchSiteDocument]);

  const viewerActions = React.useMemo(
    () => ({ onViewExtractedText, onReExtract, onReUpload, binaryLoader, onDownloadOriginal }),
    [onViewExtractedText, onReExtract, onReUpload, binaryLoader, onDownloadOriginal],
  );

  // SET-D5 — when `initialView === 'extractedText'` and the dialog is
  // opening for a fresh doc, default-open the extracted-text panel.
  // Re-fires on doc change so re-using the dialog for a different doc
  // honours the prop for each. Clears the local state on close so a
  // re-open without `initialView` doesn't accidentally re-show it.
  React.useEffect(() => {
    if (!open) {
      setExtractedTextDoc(null);
      setIsFullscreen(false);
      return;
    }
    // H-4 — a passage-offset deep-link also opens onto the extracted-text
    // view (the offset indexes `textContent`, which that view renders).
    if ((initialView === 'extractedText' || highlightOffset) && doc && doc.textContent?.trim()) {
      setExtractedTextDoc(doc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id, initialView, highlightOffset?.start, highlightOffset?.end]);

  if (!doc) return null;
  const Renderer = resolveViewer(doc);
  const hasExtractedText = !!doc.textContent?.trim();
  return (
    <>
      <Dialog open={open} onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
        <DialogContent
          className={cn(
            'flex flex-col p-0 gap-0',
            isFullscreen
              // The base dialogContentVariants hard-codes a centred, margin-
              // inset, rounded box (`fixed top-1/2 left-1/2 -translate-x-1/2
              // -translate-y-1/2 rounded-xl w-[calc(100vw-2rem)]`). Tailwind's
              // last-class-wins can't beat `-translate-*` / `top-1/2` here
              // because they're different utilities, so we use `!important`
              // variants to DEFEAT the base and pin the popup edge-to-edge.
              ? '!inset-0 !left-0 !top-0 !right-0 !bottom-0 !w-screen !h-screen !max-w-none !max-h-none !translate-x-0 !translate-y-0 !rounded-none'
              : 'w-[calc(100vw-2rem)] max-w-[min(95vw,1200px)] h-[min(90vh,900px)]',
          )}
          data-testid="doc-viewer-dialog"
          data-fullscreen={isFullscreen ? 'true' : 'false'}
        >
          <DialogHeader className="px-4 py-3 border-b border-border flex flex-row items-center justify-between">
            <DialogTitle className="text-sm font-bold truncate flex-1 min-w-0 mr-3 flex items-center gap-1.5">
              <span className="truncate">{doc.fileName}</span>
              {/* SET-D4 — only mount when caller actually has the
                   reverse-index data; report-mode callers that don't
                   know the SED state shouldn't advertise a popover
                   that would only say "Not yet extracted." */}
              {ingestionContributedSections && (
                <DocSedIngestionPopover
                  doc={doc}
                  contributedSections={ingestionContributedSections}
                  triggerSize="md"
                  testIdSuffix={`viewer-${doc.id}`}
                />
              )}
            </DialogTitle>
            {/* pr-10 leaves room for the Radix DialogContent close button (absolute top-2 right-2). */}
            <div className="flex gap-2 shrink-0 pr-10">
              <Button
                size="sm" variant="ghost" className="text-xs"
                title={isFullscreen ? 'Exit full screen' : 'Full screen'}
                aria-pressed={isFullscreen}
                onClick={() => setIsFullscreen(v => !v)}
                data-testid="doc-viewer-fullscreen"
              >
                {isFullscreen
                  ? <Minimize2 className="w-4 h-4 mr-1" aria-hidden />
                  : <Maximize2 className="w-4 h-4 mr-1" aria-hidden />}
                {isFullscreen ? 'Exit' : 'Full screen'}
              </Button>
              <Button
                size="sm" variant="ghost" className="text-xs"
                disabled={!hasExtractedText}
                /* DVF-2 — surface the recovery path in the tooltip so the
                     disabled state isn't a silent dead-end. When the doc
                     has raw bytes, the user can run a fresh extraction
                     via the in-viewer "Re-extract this document" CTA in
                     the ViewerRecovery panel. */
                title={
                  hasExtractedText
                    ? 'Show the extracted text the AI sees'
                    : doc.rawStoragePath
                      ? 'No extracted text available — re-extract this document to populate it.'
                      : 'No extracted text available — re-upload to enable re-extraction.'
                }
                onClick={() => setExtractedTextDoc(doc)}
                data-testid="doc-viewer-extracted-text"
              >
                <FileText className="w-4 h-4 mr-1" /> AI text
              </Button>
              <Button
                size="sm" variant="outline" className="text-xs"
                disabled={!doc.rawStoragePath}
                title={doc.rawStoragePath ? 'Download' : 'Re-upload to enable download'}
                onClick={async () => {
                  try { await onDownloadOriginal(doc); }
                  catch (e) { toast.error((e as Error).message || 'Download failed'); }
                }}
                data-testid="doc-viewer-download"
              >
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden bg-muted">
            <DocViewerActionsContext.Provider value={viewerActions}>
              <DocViewerErrorBoundary
                doc={doc}
                resetKey={doc.id}
                onViewExtractedText={onViewExtractedText}
                onDownloadOriginal={onDownloadOriginal}
              >
                <Suspense fallback={
                  <div className="h-full flex items-center justify-center text-text-muted text-xs" role="status" aria-live="polite">
                    <div className="animate-pulse">Loading viewer…</div>
                  </div>
                }>
                  {/* DVF-RU — `reUploadNonce` in the key forces the viewer to
                       re-mount after an in-place re-upload so useDocBytes
                       re-fetches against the freshly-stamped rawStoragePath. */}
                  <div key={`${doc.id}:${reUploadNonce}`} className="h-full min-h-0">
                    {/* REF-LINEAGE REF-3 — propagate initialPage. PdfViewer
                         honours it (1-based page jump); other viewers
                         (DocxViewer, SheetViewer, TextViewer, ImageViewer,
                         VideoViewer, FallbackViewer) accept the prop in
                         their signature but ignore the value — page jump
                         is PDF-only for now. */}
                    <Renderer doc={doc} initialPage={initialPage} />
                  </div>
                </Suspense>
              </DocViewerErrorBoundary>
            </DocViewerActionsContext.Provider>
          </div>
        </DialogContent>
      </Dialog>
      <ExtractedTextDialog
        bodyLoader={bodyLoader}
        doc={extractedTextDoc}
        open={!!extractedTextDoc}
        onClose={() => setExtractedTextDoc(null)}
        ingestionContributedSections={ingestionContributedSections}
        onReExtract={onReExtractFromExtractedText}
        highlightOffset={highlightOffset}
      />
    </>
  );
};
